import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowLeft, ArrowRight, Check, MapPin, Navigation, Plane, Car, Camera, X, Image as ImageIcon } from "lucide-react";
import { api, CATEGORIES, computePricing, TRAVEL_TIERS, resolveTravelFee, AIRPORTS } from "../lib/api";

export default function TaskNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("");
  const [addressApprox, setAddressApprox] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [pricingMode, setPricingMode] = useState("fixed");
  const [budgetHint, setBudgetHint] = useState("");
  const [distanceTier, setDistanceTier] = useState("0_5");
  const [travelFeeManual, setTravelFeeManual] = useState(15);
  const [coords, setCoords] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  // Airport shuttle / long-distance specific
  const [airportCode, setAirportCode] = useState("YUL");
  const [flightType, setFlightType] = useState("departure");
  const [flightDateTime, setFlightDateTime] = useState("");
  const [longDistanceFee, setLongDistanceFee] = useState(0);
  const [scheduledDate, setScheduledDate] = useState(""); // YYYY-MM-DD
  const [photos, setPhotos] = useState([]); // list of storage paths
  const [photoBusy, setPhotoBusy] = useState(false);
  const priceInputRef = useRef(null);
  const stepTopRef = useRef(null);

  // Smooth-scroll to top + focus price field at step 4
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (step === 4 && priceInputRef.current) {
      const t = setTimeout(() => priceInputRef.current?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [step]);

  const isAirport = category === "navette_aeroport";
  const isLongDistance = category === "course_deplacement" || category === "navette_aeroport";

  // Default airport surcharge to 15$ when shifting to navette category
  useEffect(() => {
    if (isAirport && Number(longDistanceFee) === 0) setLongDistanceFee(15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAirport]);

  const travelFee = useMemo(() => resolveTravelFee(distanceTier, travelFeeManual), [distanceTier, travelFeeManual]);
  const effectiveTravel = useMemo(
    () => travelFee + (isLongDistance ? Math.max(0, Number(longDistanceFee) || 0) : 0),
    [travelFee, isLongDistance, longDistanceFee]
  );
  const pricing = useMemo(() => computePricing(Number(basePrice) || 0, urgent, effectiveTravel), [basePrice, urgent, effectiveTravel]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Position détectée");
        setGeoBusy(false);
      },
      () => { toast.error("Impossible d'obtenir la position"); setGeoBusy(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const next = () => {
    if (step === 1 && !category) return toast.error("Choisis une catégorie");
    if (step === 2 && (!title || !description)) return toast.error("Titre et description requis");
    if (step === 2 && isAirport && (!airportCode || !flightDateTime)) return toast.error("Choisis l'aéroport et l'heure du vol");
    if (step === 3 && (!area || !addressApprox)) return toast.error("Indique ton secteur");
    setStep(step + 1);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (photos.length >= 3) {
      toast.error("Maximum 3 photos");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo trop lourde (max 5 Mo)");
      return;
    }
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/tasks/upload-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((p) => [...p, data.path]);
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x !== path));

  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("gh_token");
  const photoUrl = (path) => `${backend}/api/files/${path}?auth=${token}`;

  const submit = async () => {
    if (pricingMode === "fixed" && (!basePrice || Number(basePrice) < 5)) {
      toast.error("Indique un prix d'au moins 5 $");
      priceInputRef.current?.focus();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/tasks", {
        category, title, description, photos, area,
        address_approx: addressApprox, base_price: Number(basePrice), urgent,
        distance_tier: distanceTier,
        travel_fee_manual: distanceTier === "manual" ? Number(travelFeeManual) : null,
        client_lat: coords?.lat ?? null, client_lng: coords?.lng ?? null,
        pricing_mode: pricingMode,
        budget_hint: pricingMode === "bids" && budgetHint ? Number(budgetHint) : null,
        airport_code: isAirport ? airportCode : null,
        flight_type: isAirport ? flightType : null,
        flight_datetime: isAirport ? flightDateTime : null,
        long_distance_fee: isLongDistance && Number(longDistanceFee) > 0 ? Number(longDistanceFee) : null,
        scheduled_date: scheduledDate || null,
      });
      toast.success("Tâche publiée !");
      nav(`/tasks/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-heading font-black text-navy">Nouvelle tâche</h1>
        <span className="chip">Étape {step}/4</span>
      </div>

      <div className="h-1.5 w-full bg-surface rounded-full overflow-hidden mb-8">
        <div className="h-full bg-lime transition-all" style={{ width: `${(step / 4) * 100}%` }} />
      </div>

      {step === 1 && (
        <div className="card-surface p-6">
          <h2 className="font-heading font-black text-xl text-navy">Quelle catégorie ?</h2>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`category-${c.id}`}
                className={`p-4 rounded-xl border-2 text-left font-bold transition-all ${category === c.id ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy hover:bg-surface"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <a href="/idees" target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-navy/70 hover:text-navy underline-offset-4 hover:underline"
            data-testid="wizard-ideas-link">
            💡 Pas sûr de ta catégorie ? Vois 80+ exemples →
          </a>
        </div>
      )}

      {step === 2 && (
        <div className="card-surface p-6 space-y-4">
          <h2 className="font-heading font-black text-xl text-navy">Décris ta tâche</h2>
          <div>
            <label className="text-sm font-bold text-navy">Titre court</label>
            <input className="input-base mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex: Sortir la poubelle + petites courses" data-testid="task-title" />
          </div>
          <div>
            <label className="text-sm font-bold text-navy">Description</label>
            <textarea rows={5} className="input-base mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Donne des détails pour aider le helper à bien comprendre." data-testid="task-description" />
          </div>

          <div data-testid="task-photos-block">
            <label className="text-sm font-bold text-navy flex items-center gap-2">
              <Camera size={16} /> Photos (optionnel · max 3)
            </label>
            <p className="text-xs text-[#475569] mt-1">Une image vaut mille mots — aide les helpers à mieux comprendre.</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p} className="relative aspect-square rounded-xl overflow-hidden bg-surface border border-[#E2E8F0] group" data-testid={`task-photo-${p}`}>
                  <img src={photoUrl(p)} alt="Photo tâche" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(p)}
                    className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md"
                    aria-label="Supprimer"
                    data-testid="remove-photo"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <label className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-xs font-bold cursor-pointer transition-colors ${photoBusy ? "border-[#94A3B8] text-[#94A3B8]" : "border-navy/30 text-navy hover:border-lime hover:bg-lime/10"}`} data-testid="task-photo-add">
                  <ImageIcon size={22} />
                  <span className="mt-1">{photoBusy ? "Upload…" : "Ajouter"}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }}
                    className="hidden"
                    disabled={photoBusy}
                  />
                </label>
              )}
            </div>
          </div>

          {isAirport && (
            <div className="pt-4 mt-2 border-t border-[#E2E8F0] space-y-4" data-testid="airport-fields">
              <div className="flex items-center gap-2 text-navy font-bold">
                <Plane size={18} /> Détails du vol
              </div>
              <div>
                <label className="text-sm font-bold text-navy">Aéroport</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {AIRPORTS.map((a) => (
                    <button key={a.code} type="button" onClick={() => setAirportCode(a.code)} data-testid={`airport-${a.code}`}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${airportCode === a.code ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy hover:bg-surface"}`}>
                      <div className="font-black text-sm">{a.code}</div>
                      <div className={`text-xs mt-0.5 ${airportCode === a.code ? "text-lime" : "text-[#475569]"}`}>{a.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-navy">Sens du trajet</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { id: "departure", label: "Départ (vers l'aéroport)" },
                    { id: "arrival", label: "Arrivée (depuis l'aéroport)" },
                  ].map((f) => (
                    <button key={f.id} type="button" onClick={() => setFlightType(f.id)} data-testid={`flight-type-${f.id}`}
                      className={`p-3 rounded-xl border-2 text-left text-sm font-bold transition-all ${flightType === f.id ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy hover:bg-surface"}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-navy">Date et heure du vol</label>
                <input type="datetime-local" className="input-base mt-1" value={flightDateTime}
                  onChange={(e) => setFlightDateTime(e.target.value)} data-testid="flight-datetime" />
                <div className="text-xs text-[#475569] mt-1">Le helper arrivera environ 30 min avant pour un départ.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="card-surface p-6 space-y-4">
          <h2 className="font-heading font-black text-xl text-navy">Où es-tu ?</h2>
          <p className="text-sm text-[#475569]">On affichera seulement l'adresse approximative jusqu'à confirmation.</p>
          <div>
            <label className="text-sm font-bold text-navy">Secteur / Ville</label>
            <input className="input-base mt-1" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Montréal - Plateau" data-testid="task-area" />
          </div>
          <div>
            <label className="text-sm font-bold text-navy">Adresse approximative</label>
            <input className="input-base mt-1" value={addressApprox} onChange={(e) => setAddressApprox(e.target.value)} placeholder="Près du Parc Lafontaine" data-testid="task-address" />
          </div>

          {!isAirport && (
            <div>
              <label className="text-sm font-bold text-navy">Date souhaitée (optionnel)</label>
              <input
                type="date"
                className="input-base mt-1"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="task-scheduled-date"
              />
              <p className="text-xs text-[#475569] mt-1">Quand veux-tu que la tâche soit faite ? Apparaîtra dans le calendrier.</p>
            </div>
          )}

          <div className="pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-navy">Distance approximative</div>
                <div className="text-xs text-[#475569]">Frais de déplacement payés au helper.</div>
              </div>
              <button type="button" onClick={useMyLocation} disabled={geoBusy}
                className="text-xs font-bold text-navy bg-surface hover:bg-[#E2E8F0] rounded-full px-3 py-2 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-geolocate">
                <Navigation size={14} /> {coords ? "Position OK" : geoBusy ? "..." : "Ma position"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {TRAVEL_TIERS.map((t) => (
                <button key={t.id} type="button" onClick={() => setDistanceTier(t.id)} data-testid={`tier-${t.id}`}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${distanceTier === t.id ? "border-navy bg-navy text-white" : "border-[#E2E8F0] text-navy hover:bg-surface"}`}>
                  <div className="font-bold text-sm flex items-center gap-1"><MapPin size={14} /> {t.label}</div>
                  <div className={`text-xs mt-0.5 ${distanceTier === t.id ? "text-lime" : "text-[#475569]"}`}>{t.hint}</div>
                </button>
              ))}
            </div>
            {distanceTier === "manual" && (
              <div className="mt-3">
                <label className="text-sm font-bold text-navy">Frais de déplacement ($)</label>
                <input type="number" min="0" className="input-base mt-1" value={travelFeeManual}
                  onChange={(e) => setTravelFeeManual(e.target.value)} data-testid="travel-manual" />
                <div className="text-xs text-[#475569] mt-1">À discuter avec le helper si très éloigné.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card-surface p-6 space-y-6">
          <h2 className="font-heading font-black text-xl text-navy">Ton prix</h2>

          <div className="grid grid-cols-2 gap-2 p-1 bg-surface rounded-full" data-testid="pricing-mode">
            {[
              { id: "fixed", label: "Prix fixe" },
              { id: "bids", label: "Soumissions" },
            ].map((m) => (
              <button key={m.id} type="button" onClick={() => setPricingMode(m.id)}
                className={`py-2 rounded-full font-bold text-sm transition-all ${pricingMode === m.id ? "bg-navy text-white" : "text-navy"}`}
                data-testid={`mode-${m.id}`}>
                {m.label}
              </button>
            ))}
          </div>

          {pricingMode === "fixed" ? (
            <div>
              <label className="text-sm font-bold text-navy">Prix de base ($) <span className="text-red-600">*</span></label>
              <input
                ref={priceInputRef}
                type="number"
                inputMode="decimal"
                min="5"
                step="1"
                className="input-base mt-1 text-2xl font-black"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                placeholder="Ex: 30"
                data-testid="task-price"
                autoComplete="off"
              />
              <p className="text-xs text-[#475569] mt-1">Ce montant ira directement au helper qui accepte. Minimum 5 $.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-lime/10 border-2 border-lime rounded-xl p-4">
                <div className="font-bold text-navy">💡 Mode soumissions</div>
                <p className="text-sm text-[#475569] mt-1">Les helpers de ta région vont te proposer un prix. Tu choisis la meilleure offre.</p>
              </div>
              <div>
                <label className="text-sm font-bold text-navy">Budget indicatif ($) — optionnel</label>
                <input type="number" min="0" className="input-base mt-1" value={budgetHint} onChange={(e) => setBudgetHint(e.target.value)} placeholder="Ex: 40" data-testid="budget-hint" />
                <p className="text-xs text-[#475569] mt-1">Aide les helpers à cibler leur offre. Tu ne paies rien tant que tu n'as pas accepté une soumission.</p>
              </div>
            </div>
          )}

          <button type="button" onClick={() => setUrgent(!urgent)} data-testid="toggle-urgent"
            className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${urgent ? "border-lime bg-lime/10" : "border-[#E2E8F0]"}`}>
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${urgent ? "bg-lime text-navy" : "bg-surface text-navy"}`}>
              <Zap size={20} />
            </div>
            <div className="text-left flex-1">
              <div className="font-bold text-navy">Tâche urgente (+30 %)</div>
              <div className="text-xs text-[#475569]">Les helpers verront ta tâche en priorité</div>
            </div>
            {urgent && <Check className="text-lime" />}
          </button>

          {isLongDistance && (
            <div className="p-4 rounded-xl border-2 border-[#E2E8F0]" data-testid="long-distance-fee-block">
              <div className="flex items-center gap-2 text-navy font-bold">
                {isAirport ? <Plane size={16} /> : <Car size={16} />}
                Forfait longue distance ($)
              </div>
              <input type="number" min="0" step="5" className="input-base mt-2"
                value={longDistanceFee}
                onChange={(e) => setLongDistanceFee(e.target.value)}
                placeholder={isAirport ? "Ex: 25" : "Ex: 30"}
                data-testid="long-distance-fee" />
              <div className="text-xs text-[#475569] mt-1">
                {isAirport
                  ? "S'ajoute aux frais de déplacement et va au helper. Ex: 25 $ pour navette aéroport."
                  : "S'ajoute aux frais de déplacement et va au helper. Recommandé pour trajets >25 km."}
              </div>
            </div>
          )}

          {pricingMode === "fixed" && (
            <div className="bg-surface rounded-xl p-4 font-mono text-sm" data-testid="price-summary">
              <Row l="Prix de base" v={`${pricing.base_price.toFixed(2)} $`} />
              {urgent && <Row l="Urgence" v={`+${pricing.urgency_fee.toFixed(2)} $`} />}
              {travelFee > 0 && <Row l="Frais déplacement" v={`+${travelFee.toFixed(2)} $`} />}
              {isLongDistance && Number(longDistanceFee) > 0 && (
                <Row l={isAirport ? "Forfait aéroport" : "Forfait longue distance"} v={`+${Number(longDistanceFee).toFixed(2)} $`} />
              )}
              <Row l="Sous-total helper" v={`${pricing.helper_subtotal.toFixed(2)} $`} bold />
              <Row l={`Frais Go Helper (3 $ + 15 %)`} v={`${pricing.go_helper_fee.toFixed(2)} $`} />
              <div className="border-t border-[#CBD5E1] mt-2 pt-2 flex justify-between">
                <span className="font-black text-navy">Total à payer</span>
                <span className="font-heading font-black text-navy text-xl" data-testid="total-amount">{pricing.total.toFixed(2)} $</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        {step > 1 ? (
          <button onClick={() => setStep(step - 1)} className="btn-outline" data-testid="btn-back">
            <ArrowLeft size={18} /> Retour
          </button>
        ) : <div />}
        {step < 4 ? (
          <button onClick={next} className="btn-primary" data-testid="wizard-next">Suivant <ArrowRight size={18} /></button>
        ) : (
          <button
            onClick={submit}
            disabled={loading || (pricingMode === "fixed" && (!basePrice || Number(basePrice) < 5))}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="wizard-submit"
          >
            {loading ? "..." : "Publier la tâche"} <Check size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? "font-bold text-navy" : "text-[#475569]"}`}>
      <span>{l}</span><span>{v}</span>
    </div>
  );
}
